import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ChevronLeft, Loader2, BadgeCheck } from "lucide-react";
import { api } from "../lib/api";

export function VerifiedBadge({ color, size = 16, testid = "verified-badge" }) {
  if (!color) return null;
  const cls = color === "blue" ? "text-[#2F80FF]" : "text-success";
  return <BadgeCheck data-testid={testid} size={size} className={cls} fill="currentColor" stroke="white" strokeWidth={2} />;
}

// Single, consistent blue verified check used everywhere a user is verified.
// Always the SAME blue (#2F80FF) — never varies.
export function BlueCheck({ show = true, size = 15, testid = "blue-check" }) {
  if (!show) return null;
  return (
    <BadgeCheck data-testid={testid} size={size} className="text-[#2F80FF] shrink-0"
      fill="currentColor" stroke="white" strokeWidth={2.4} />
  );
}

export function Spinner({ className = "" }) {
  return <Loader2 className={`animate-spin ${className}`} />;
}

// Small, elegant "Verified account" chip showing the current user's blue check.
export function VerifiedChip({ verified, testid = "verified-chip" }) {
  if (!verified) return null;
  return (
    <div data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-full bg-[#2F80FF]/10 border border-[#2F80FF]/25 px-3 py-1.5">
      <BlueCheck size={15} />
      <span className="text-xs font-semibold text-[#2F80FF]">Verified account</span>
    </div>
  );
}

export function AuthedAvatar({ src, name, size = 48, className = "" }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    if (!src) { setUrl(null); return; }
    if (/^https?:\/\//.test(src) || src.startsWith("data:") || src.startsWith("blob:")) {
      setUrl(src); return;
    }
    let active = true; let objUrl;
    (async () => {
      try {
        const path = src.startsWith("/api") ? src.slice(4) : src;
        const r = await api.get(path, { responseType: "blob" });
        objUrl = URL.createObjectURL(r.data);
        if (active) setUrl(objUrl);
      } catch { if (active) setFailed(true); }
    })();
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [src]);

  if (src && url && !failed) {
    return <img src={url} alt="" className={`object-cover ${className}`} style={{ width: size, height: size }} />;
  }
  return (
    <div className={`grid place-items-center bg-primary/15 text-primary font-semibold ${className}`} style={{ width: size, height: size }}>
      {(name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

export function AuthedImg({ src, className = "", testid }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!src) { setUrl(null); return; }
    if (/^https?:\/\//.test(src) || src.startsWith("data:") || src.startsWith("blob:")) { setUrl(src); return; }
    let active = true; let objUrl;
    (async () => {
      try {
        const path = src.startsWith("/api") ? src.slice(4) : src;
        const r = await api.get(path, { responseType: "blob" });
        objUrl = URL.createObjectURL(r.data);
        if (active) setUrl(objUrl);
      } catch { /* ignore */ }
    })();
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [src]);
  return <img data-testid={testid} src={url || ""} alt="" className={className} />;
}

export function FullLoader({ label = "Loading" }) {
  return (
    <div className="min-h-[60vh] grid place-items-center text-muted-foreground">
      <div className="flex flex-col items-center gap-3">
        <Spinner className="w-7 h-7 text-primary" />
        <span className="text-sm">{label}…</span>
      </div>
    </div>
  );
}

const STATUS_STYLES = {
  "CONNECTED": "bg-success/10 text-success border-success/20",
  "HEALTHY": "bg-success/10 text-success border-success/20",
  "NOT CONNECTED": "bg-muted text-muted-foreground border-border",
  "SETUP REQUIRED": "bg-warning/10 text-warning border-warning/30",
  "DISABLED": "bg-muted text-muted-foreground border-border",
  "ERROR": "bg-destructive/10 text-destructive border-destructive/20",
};

export function StatusBadge({ label }) {
  const cls = STATUS_STYLES[label] || "bg-muted text-muted-foreground border-border";
  return (
    <span data-testid="status-badge" className={`inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wide px-2.5 py-1 rounded-full border ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}

export function PageHeader({ title, subtitle, action }) {
  const nav = useNavigate();
  return (
    <div className="flex items-center gap-3 mb-6">
      <button data-testid="back-btn" onClick={() => nav(-1)}
        className="grid place-items-center w-10 h-10 rounded-full bg-secondary text-primary transition-transform duration-200 active:scale-90">
        <ChevronLeft size={20} />
      </button>
      <div className="flex-1">
        <h1 className="font-display text-2xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, body, children }) {
  return (
    <div className="text-center py-14 px-6 animate-fade-up">
      {Icon && (
        <div className="mx-auto mb-4 grid place-items-center w-16 h-16 rounded-2xl bg-secondary text-primary">
          <Icon size={28} />
        </div>
      )}
      <h3 className="font-display text-lg font-medium">{title}</h3>
      {body && <p className="text-sm text-muted-foreground mt-1 max-w-xs mx-auto">{body}</p>}
      {children && <div className="mt-5">{children}</div>}
    </div>
  );
}

export function ProviderNotice({ name, label, message }) {
  return (
    <div data-testid="provider-notice" className="rounded-2xl border border-dashed border-border bg-card p-6 text-center">
      <div className="font-display text-sm tracking-[0.2em] uppercase text-muted-foreground">{name}</div>
      <div className="my-3"><StatusBadge label={label} /></div>
      <p className="text-sm text-muted-foreground max-w-sm mx-auto">{message}</p>
    </div>
  );
}
